import type { AuthProvider } from '@refinedev/core';
import { authSession } from '@/api/axios';
import type { AuthSession } from '@/api/authSession';

export function safeReturnTo(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') ||
    /[\\\s]|%(?:2f|5c|0[ad])/i.test(value)) return '/';
  const url = new URL(value, 'https://local.invalid');
  if (url.origin !== 'https://local.invalid' || url.pathname === '/login' || url.pathname.startsWith('/auth/')) return '/';
  return url.pathname + url.search + url.hash;
}

export function createAuthProvider(session: AuthSession): AuthProvider {
  return {
    login: async ({ username, password, redirectTo }) => {
      try {
        await session.login({ username, password });
        return { success: true, redirectTo: session.getSnapshot().user?.must_change_password
          ? '/auth/change-password' : safeReturnTo(redirectTo) };
      } catch (error) {
        return { success: false, error: error as Error };
      }
    },
    logout: async () => {
      try {
        await session.logout();
        return { success: true, redirectTo: '/login' };
      } catch {
        return { success: false, error: new Error('Sign out could not be confirmed; retry') };
      }
    },
    check: async () => {
      await session.bootstrap();
      return session.getSnapshot().status === 'authenticated' ? { authenticated: true } :
        { authenticated: false, redirectTo: '/login' };
    },
    onError: async error => {
      if ((error.statusCode || error.response?.status) === 401) return { redirectTo: '/login', error };
      return { error };
    },
    getPermissions: async () => {
      await session.bootstrap();
      return session.getSnapshot().user?.permissions || [];
    },
    getIdentity: async () => {
      await session.bootstrap();
      const user = session.getSnapshot().user;
      return user ? { ...user, name: user.full_name || user.username } : null;
    },
    updatePassword: async ({ currentPassword, password }) => {
      try {
        await session.changePassword(currentPassword, password);
        return { success: true, redirectTo: '/login' };
      } catch {
        return { success: false, error: new Error('Password change failed') };
      }
    },
  };
}

export const authProvider = createAuthProvider(authSession);
